import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import api, { getSocket } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useSeason } from '../../context/SeasonContext';
import { mergeStandingsUsers } from '../../utils/standingsUtils';
import {
  findDefaultWeekStructureIndex,
  formatWeekStructureLabel,
  getOverviewCellVisibility,
  getOverviewStaleMessage,
  getUserWeekStatuses,
  shouldShowOverviewStaleBanner,
} from '../../utils/overviewUtils';
import MobileOverviewStatusChip from './MobileOverviewStatusChip';
import styles from './MobileLeagueOverview.module.css';

const EMPTY_WEEK_STRUCTURE = [];

const MobileLeagueOverview = ({ season, leaderboardData, onRefreshLeaderboard }) => {
  const [viewMode, setViewMode] = useState('by-week');
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allActiveUsers, setAllActiveUsers] = useState([]);

  const { user } = useAuth();
  const { settings } = useLeagueSettings();
  const { locksCache, getMaxPicksForWeek } = useGameContext();
  const { currentWeek } = useCurrentWeek();
  const { effectiveSeason } = useSeason();

  const weekStructure = useMemo(
    () => leaderboardData?.weekStructure ?? EMPTY_WEEK_STRUCTURE,
    [leaderboardData?.weekStructure]
  );

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const response = await api.getActiveUsers();
        if (response.data && Array.isArray(response.data)) {
          setAllActiveUsers(response.data);
        }
      } catch (err) {
        console.error('Error fetching active users for overview:', err);
        setAllActiveUsers([]);
      }
    };

    fetchActiveUsers();
  }, []);

  useEffect(() => {
    if (weekStructure.length > 0) {
      setSelectedWeekIndex(findDefaultWeekStructureIndex(weekStructure, currentWeek));
    }
  }, [weekStructure, currentWeek, season]);

  useEffect(() => {
    if (!selectedUser && allActiveUsers.length > 0) {
      const current = allActiveUsers.find((name) => name === user?.username);
      setSelectedUser(current || allActiveUsers[0]);
    }
  }, [allActiveUsers, selectedUser, user?.username]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !onRefreshLeaderboard) return undefined;

    let refreshTimer;
    const handleLiveUpdates = () => {
      if (season !== effectiveSeason) return;

      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        onRefreshLeaderboard();
      }, 500);
    };

    socket.on('live_game_updates', handleLiveUpdates);
    return () => {
      clearTimeout(refreshTimer);
      socket.off('live_game_updates', handleLiveUpdates);
    };
  }, [season, effectiveSeason, onRefreshLeaderboard]);

  const hasReachedMaxPicks = useMemo(() => {
    if (!currentWeek || !locksCache[currentWeek]) return false;

    const locksForWeek = locksCache[currentWeek];
    const userLocksCount = locksForWeek.filter(
      (lock) =>
        lock.userId === user?.id &&
        (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    return userLocksCount >= getMaxPicksForWeek(currentWeek);
  }, [currentWeek, locksCache, user?.id, getMaxPicksForWeek]);

  const users = useMemo(
    () =>
      mergeStandingsUsers(
        leaderboardData?.users,
        allActiveUsers,
        settings,
        weekStructure
      ),
    [leaderboardData, allActiveUsers, settings, weekStructure]
  );

  const hasThisUserLockedWeek = useCallback(
    (weekNumber) => {
      if (!locksCache[weekNumber]) return false;
      return locksCache[weekNumber].some(
        (lock) =>
          lock.userId === user?.id &&
          (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
      );
    },
    [locksCache, user?.id]
  );

  const hasOtherUserLockedFutureWeek = useCallback(
    (weekNumber) =>
      locksCache[weekNumber]?.some(
        (lock) => lock.userId !== user?.id && lock.status === 'locked'
      ) ?? false,
    [locksCache, user?.id]
  );

  const getCellVisibility = useCallback(
    (weekNumber) =>
      getOverviewCellVisibility({
        weekNumber,
        currentWeek,
        season,
        effectiveSeason,
        settings,
        hasReachedMaxPicks,
        hasThisUserLockedWeek: hasThisUserLockedWeek(weekNumber),
        hasOtherUserLockedFutureWeek: hasOtherUserLockedFutureWeek(weekNumber),
      }),
    [
      currentWeek,
      season,
      effectiveSeason,
      settings,
      hasReachedMaxPicks,
      hasThisUserLockedWeek,
      hasOtherUserLockedFutureWeek,
    ]
  );

  const selectedWeekSlot = weekStructure[selectedWeekIndex];

  const getStatusForUserAtSelectedWeek = (player) => {
    const weeks = getUserWeekStatuses(player, weekStructure);
    return weeks[selectedWeekIndex]?.status || 'none';
  };

  const handleWeekNavigate = (weekIndex) => {
    setSelectedWeekIndex(weekIndex);
    setViewMode('by-week');
  };

  const handleUserNavigate = (username) => {
    setSelectedUser(username);
    setViewMode('by-user');
  };

  if (leaderboardData && !leaderboardData.seasonStarted) {
    return (
      <div className={styles.emptyState}>
        <h2>League Overview</h2>
        <p>{leaderboardData.message || 'Season has not started yet.'}</p>
      </div>
    );
  }

  if (!weekStructure.length || !users.length) {
    return (
      <div className={styles.emptyState}>
        <h2>League Overview</h2>
        <p>No overview data available.</p>
      </div>
    );
  }

  const showStaleBanner = shouldShowOverviewStaleBanner({
    hasReachedMaxPicks,
    showLocks: settings.show_locks,
    showLockers: settings.show_lockers,
    season,
    effectiveSeason,
  });

  const renderModeToggle = () => (
    <div className={styles.modeToggle}>
      <button
        type="button"
        className={`${styles.modeButton} ${viewMode === 'by-week' ? styles.modeActive : ''}`}
        onClick={() => setViewMode('by-week')}
      >
        By Week
      </button>
      <button
        type="button"
        className={`${styles.modeButton} ${viewMode === 'by-user' ? styles.modeActive : ''}`}
        onClick={() => setViewMode('by-user')}
      >
        By User
      </button>
    </div>
  );

  const renderByWeek = () => (
    <>
      <div className={styles.selectorRow}>
        <label htmlFor="overview-week-select">Week</label>
        <select
          id="overview-week-select"
          value={selectedWeekIndex}
          onChange={(e) => setSelectedWeekIndex(parseInt(e.target.value, 10))}
        >
          {weekStructure.map((week, index) => (
            <option key={`${week.weekNumber}-${week.pickNumber}-${index}`} value={index}>
              {formatWeekStructureLabel(week)}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.cardList}>
        {users.map((player) => {
          const status = getStatusForUserAtSelectedWeek(player);
          const weekNumber = selectedWeekSlot?.weekNumber || 1;
          const visibility = getCellVisibility(weekNumber);
          const isCurrentUser = player.username === user?.username;

          return (
            <div
              key={player.username}
              className={`${styles.rowCard} ${isCurrentUser ? styles.currentUser : ''}`}
            >
              <div className={styles.rowHeader}>
                <span className={styles.rank}>{player.rank ?? '-'}</span>
                <button
                  type="button"
                  className={styles.usernameLink}
                  onClick={() => handleUserNavigate(player.username)}
                >
                  {player.username}
                </button>
                <MobileOverviewStatusChip
                  status={status}
                  visibility={visibility}
                  variant="hero"
                />
                <span className={styles.record}>
                  {player.wins || 0}-{player.losses || 0}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderByUser = () => {
    const player = users.find((entry) => entry.username === selectedUser) || users[0];
    const weekStatuses = getUserWeekStatuses(player, weekStructure);

    return (
      <>
        <div className={styles.selectorRow}>
          <label htmlFor="overview-user-select">User</label>
          <select
            id="overview-user-select"
            value={player?.username || ''}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            {users.map((entry) => (
              <option key={entry.username} value={entry.username}>
                {entry.username}
              </option>
            ))}
          </select>
        </div>

        {player && (
          <div className={styles.profileStrip}>
            <span>Rank {player.rank ?? '-'}</span>
            <span>{player.wins || 0}-{player.losses || 0}</span>
          </div>
        )}

        <div className={styles.timeline}>
          {weekStatuses.map((week, index) => {
            const visibility = getCellVisibility(week.weekNumber);
            return (
              <div key={`${week.weekNumber}-${week.pickNumber}-${index}`} className={styles.weekRow}>
                <button
                  type="button"
                  className={styles.weekLink}
                  onClick={() => handleWeekNavigate(index)}
                >
                  {formatWeekStructureLabel(week)}
                </button>
                <MobileOverviewStatusChip
                  status={week.status}
                  visibility={visibility}
                  variant="hero"
                />
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className={styles.container}>
      {showStaleBanner && (
        <div className={styles.staleBanner}>{getOverviewStaleMessage(currentWeek)}</div>
      )}
      {renderModeToggle()}
      <p className={styles.hint}>
        {viewMode === 'by-week'
          ? 'Weekly results for every player. Tap a name for their full season.'
          : 'Season timeline for one player. Tap a week to see the whole league.'}
      </p>
      {viewMode === 'by-week' ? renderByWeek() : renderByUser()}
    </div>
  );
};

MobileLeagueOverview.propTypes = {
  season: PropTypes.number.isRequired,
  leaderboardData: PropTypes.object,
  onRefreshLeaderboard: PropTypes.func,
};

export default MobileLeagueOverview;
